/**
 * Homepage "Shop by Activity" tiles on a partner market (Thailand partner
 * feedback, 2026-10-19). The COURT / STUDIO / TRAIN / LOUNGE / RUN tiles are
 * EQL's Vietnam photos; a partner can hide the row or put their own photo
 * on each tile. The setting rides inside MarketContent.homepageSections
 * (like blocks / nav / footerLinks — no migration) under `activityTiles`.
 *
 * Pure module: the storefront renderer, the content editor and its action
 * share one source of truth. Absent / garbage → defaults (row shown, EQL
 * photos), so the Vietnam surface and every market without the setting
 * render exactly as before.
 */
import { safeHttpUrl } from "~/utils/safeUrl";

export const ACTIVITY_TILE_KEYS = ["court", "studio", "train", "lounge", "run"] as const;
export type ActivityTileKey = (typeof ACTIVITY_TILE_KEYS)[number];

export interface ActivityTilesSetting {
  /** Row shown at all (default true). */
  show: boolean;
  /** Per-tile photo override — absolute http(s) URL (R2 upload or pasted). */
  images: Partial<Record<ActivityTileKey, string>>;
}

export const DEFAULT_ACTIVITY_TILES: ActivityTilesSetting = { show: true, images: {} };

const isKey = (k: string): k is ActivityTileKey => (ACTIVITY_TILE_KEYS as readonly string[]).includes(k);

/** Sanitize an editor payload: boolean toggle + http(s) image URLs only. */
export function cleanActivityTiles(raw: unknown): ActivityTilesSetting {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { ...DEFAULT_ACTIVITY_TILES, images: {} };
  const r = raw as { show?: unknown; images?: unknown };
  const images: Partial<Record<ActivityTileKey, string>> = {};
  if (r.images && typeof r.images === "object" && !Array.isArray(r.images)) {
    for (const [k, v] of Object.entries(r.images as Record<string, unknown>)) {
      if (!isKey(k)) continue;
      const url = safeHttpUrl(v);
      if (url) images[k] = url;
    }
  }
  return { show: r.show !== false, images };
}

/** True when the setting equals the defaults — nothing worth storing. */
export function isDefaultActivityTiles(setting: ActivityTilesSetting): boolean {
  return setting.show && Object.keys(setting.images).length === 0;
}

/** Read the setting out of a stored homepageSections JSON (object shape). */
export function readActivityTiles(homepageSections: unknown): ActivityTilesSetting {
  if (!homepageSections || typeof homepageSections !== "object" || Array.isArray(homepageSections)) {
    return { ...DEFAULT_ACTIVITY_TILES, images: {} };
  }
  return cleanActivityTiles((homepageSections as Record<string, unknown>).activityTiles);
}

/**
 * Apply the setting to the default tile list: null = render nothing; otherwise
 * each tile keeps its default `img` unless the partner uploaded one.
 */
export function resolveActivityTiles<T extends { key: ActivityTileKey; img: string }>(
  defaults: readonly T[],
  setting: ActivityTilesSetting,
): T[] | null {
  if (!setting.show) return null;
  return defaults.map((tile) => {
    const override = setting.images[tile.key];
    return override ? { ...tile, img: override } : tile;
  });
}
